import React, { useEffect, useState } from "react";
import { Mic2, Sparkles, ExternalLink, RefreshCw, PlusCircle, Radio, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const PACKAGES = [
  { id: "show_1min", label: "1-min AI Show", desc: "~160 words · quick tease/promo" },
  { id: "show_3min", label: "3-min AI Show", desc: "~450 words · full segment" },
];

/**
 * Owner-only "self-produce" — mints a wizard token without Stripe so the
 * owner can generate AI shows for their own brands (e.g. TheBudgetBrain) at
 * zero out-of-pocket cost. Uses only the Emergent LLM key already in the pod.
 */
export default function OwnerProduceCard() {
  const [businessName, setBusinessName] = useState("TheBudgetBrain");
  const [pkg, setPkg] = useState("show_3min");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stations, setStations] = useState([]);
  const [attachTarget, setAttachTarget] = useState({});   // { [order_id]: station_id }
  const [attaching, setAttaching] = useState(null);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/produce/orders?source=admin_self", { headers: authHeaders() });
      setOrders(data || []);
    } catch (_) { setOrders([]); }
    finally { setLoading(false); }
  };
  const loadStations = async () => {
    try {
      const { data } = await api.get("/stations");
      setStations(data || []);
    } catch (_) { setStations([]); }
  };
  useEffect(() => { loadOrders(); loadStations(); }, []);

  const start = async () => {
    if (!businessName.trim()) { setErr("Add a brand name."); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/produce/start", {
        package_id: pkg,
        business_name: businessName.trim(),
        origin_url: window.location.origin,
      }, { headers: authHeaders() });
      // Open the wizard so the owner can answer the 6 questions immediately
      window.open(data.wizard_url, "_blank", "noopener");
      loadOrders();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not start produce order.");
    } finally { setBusy(false); }
  };

  const attach = async (orderId) => {
    const sid = attachTarget[orderId];
    if (!sid) { alert("Pick a station first."); return; }
    setAttaching(orderId);
    try {
      await api.post(`/admin/produce/${orderId}/attach-to-station`,
        { station_id: sid, kind: "talk" },
        { headers: authHeaders() });
      alert(`Attached to ${sid} — it will start airing in the rotation within a minute.`);
      loadOrders();
    } catch (e) {
      alert(e?.response?.data?.detail || "Attach failed.");
    } finally { setAttaching(null); }
  };

  const remove = async (orderId, brand) => {
    if (!window.confirm(`Delete this show clip for "${brand}"? This removes the audio and any station attachments. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/produce/orders/${orderId}`, { headers: authHeaders() });
      loadOrders();
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <section className="fl-card p-6 mt-6" data-testid="owner-produce-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-fuchsia-500/15 border border-fuchsia-400/30 flex items-center justify-center">
            <Mic2 className="w-5 h-5 text-fuchsia-400" />
          </div>
          <div>
            <h2 className="fl-display text-xl font-bold text-white">AI show producer — owner brands</h2>
            <p className="text-white/50 text-sm mt-0.5">
              Generate AI-written, AI-voiced shows for your own brands (TheBudgetBrain, etc.) with zero Stripe checkout. Skips payment entirely.
            </p>
          </div>
        </div>
        <button onClick={loadOrders} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="owner-produce-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Start a new show */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="block md:col-span-2">
          <span className="text-xs uppercase tracking-widest text-white/50 font-semibold">Brand name</span>
          <input className="fl-input mt-1 w-full" value={businessName} onChange={e => setBusinessName(e.target.value)} data-testid="owner-produce-brand" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-white/50 font-semibold">Length</span>
          <select className="fl-input mt-1 w-full" value={pkg} onChange={e => setPkg(e.target.value)} data-testid="owner-produce-package">
            {PACKAGES.map(p => (<option key={p.id} value={p.id}>{p.label}</option>))}
          </select>
        </label>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button onClick={start} disabled={busy || !businessName.trim()} className="fl-btn" data-testid="owner-produce-start">
          <Sparkles className="w-4 h-4" /> {busy ? "Starting…" : "Start & open wizard"}
        </button>
        {err && <div className="text-sm text-red-300">{err}</div>}
      </div>

      {/* Existing owner-produced orders */}
      <div className="mt-8">
        <div className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-3">Your AI shows</div>
        {loading ? (
          <div className="text-white/40 text-sm">Loading…</div>
        ) : orders.length === 0 ? (
          <div className="text-white/40 text-sm">No owner-produced shows yet. Start your first one above.</div>
        ) : (
          <div className="space-y-3" data-testid="owner-produce-list">
            {orders.map(o => {
              const ready = o.status === "ready" && !!o.audio_filename;
              return (
                <div key={o.order_id} className="fl-card p-4" data-testid={`owner-produce-${o.order_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-white font-semibold text-sm truncate">{o.business_name}</div>
                      <div className="text-white/50 text-xs mt-0.5">
                        {o.package_id} · status={o.status}{o.audio_duration ? ` · ${Math.round(o.audio_duration)}s` : ""}
                      </div>
                      {o.episode_title && (
                        <div className="text-fuchsia-300 text-xs mt-0.5 truncate">“{o.episode_title}”</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {!ready ? (
                        <a href={`/produce/wizard/${o.wizard_token}`} target="_blank" rel="noreferrer" className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid={`owner-produce-open-${o.order_id}`}>
                          <ExternalLink className="w-3.5 h-3.5" /> Open wizard
                        </a>
                      ) : (
                        <>
                          <select
                            className="fl-input !py-1.5 !text-xs"
                            value={attachTarget[o.order_id] || ""}
                            onChange={e => setAttachTarget({ ...attachTarget, [o.order_id]: e.target.value })}
                            data-testid={`owner-produce-station-${o.order_id}`}
                          >
                            <option value="">Pick station…</option>
                            {stations.map(s => (<option key={s.station_id} value={s.station_id}>{s.name}</option>))}
                          </select>
                          <button
                            disabled={attaching === o.order_id || !attachTarget[o.order_id]}
                            onClick={() => attach(o.order_id)}
                            className="fl-btn !py-1.5 !px-3 text-xs"
                            data-testid={`owner-produce-attach-${o.order_id}`}
                          >
                            <PlusCircle className="w-3.5 h-3.5" /> {attaching === o.order_id ? "Adding…" : "Add to station"}
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => remove(o.order_id, o.business_name)}
                        className="fl-btn ghost !py-1.5 !px-2.5 hover:!border-red-400/40 hover:!text-red-300"
                        data-testid={`owner-produce-delete-${o.order_id}`}
                        title="Delete this show clip"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {ready && o.audio_filename && (
                    <audio controls src={`${window.location.origin}/api/audio/${o.audio_filename}`} className="mt-3 w-full h-9" data-testid={`owner-produce-audio-${o.order_id}`} />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-6 text-[11px] text-white/40 flex items-start gap-2">
        <Radio className="w-3.5 h-3.5 mt-0.5 shrink-0 text-fuchsia-400" />
        <span>
          Shows are voiced with OpenAI HD TTS and scripted with Claude — both billed to your Emergent Universal Key at fractions of a cent per show.
          Attaching a ready show adds it as a talk track on the chosen station&apos;s rotation.
        </span>
      </div>
    </section>
  );
}
